import { useEffect, useRef, useState } from 'react'
import { useCountUp } from '../hooks/useCountUp.js'

/* =========================================================================
   ImpactMetrics — the four figures the ADLC loop is worth, shown as animated
   callouts inside the "why clients choose Spectranox" section.
   ========================================================================= */

/**
 * `delivery` and `saved`-style figures come from copy the site already
 * publishes (45-day delivery, six shipped showcase projects). `success` and
 * `experience` are placeholders — swap them for audited numbers before launch.
 */
const IMPACT = [
  {
    key: 'delivery',
    kind: 'calendar',
    value: 45,
    suffix: '',
    label: 'Day delivery',
    sub: 'Ship in 45 days, not 12 months',
    accent: '#38bdf8',
  },
  {
    key: 'success',
    kind: 'bars',
    value: 98,
    suffix: '%',
    label: 'Project deliver rate',
    sub: 'delivered on scope & schedule',
    accent: '#008eff',
    bars: [34, 48, 42, 66, 78, 100],
  },
  {
    key: 'experience',
    kind: 'review',
    value: 96,
    suffix: '%',
    label: 'User experience score',
    sub: 'usability & accessibility audits',
    accent: '#0062ad',
  },
]

/* ------------------------------------------------------------ indicators */

/** Calendar with a ticked date — the 45-day delivery promise. */
function CalendarMark({ accent }) {
  return (
    <svg viewBox="0 0 56 56" className="h-14 w-14 flex-none" fill="none" stroke={accent} strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
      <rect x="7" y="12" width="42" height="36" rx="5" />
      <path d="M7 23h42" />
      <path d="M18 7v9M38 7v9" />
      <path d="m21 35 5 5 10-11" strokeWidth="3" />
    </svg>
  )
}

/** Reviewer with a star — the user-experience score. */
function ReviewMark({ accent }) {
  return (
    <svg viewBox="0 0 56 56" className="h-14 w-14 flex-none" fill="none" stroke={accent} strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="23" cy="18" r="7.5" />
      <path d="M9 45a14 14 0 0 1 28 0" />
      <path d="m42 10 2.6 5.5 5.9.8-4.3 4.2 1.1 6-5.3-2.9-5.3 2.9 1.1-6-4.3-4.2 5.9-.8L42 10Z" fill={accent} stroke="none" />
    </svg>
  )
}

function ImpactCallout({ metric, revealed, index }) {
  const count = useCountUp(metric.value, { duration: 1500, start: revealed })
  const shown = revealed ? count : 0

  return (
    <li className="sa-callout relative overflow-hidden rounded-2xl border border-slate-200/80 bg-white p-4 shadow-sm" style={{ '--i': index }}>
      <span
        aria-hidden
        className="pointer-events-none absolute -right-8 -top-8 h-24 w-24 rounded-full blur-2xl"
        style={{ background: metric.accent, opacity: 0.14 }}
      />

      <div className="flex items-center gap-3">
        {metric.kind === 'calendar' && <CalendarMark accent={metric.accent} />}
        {metric.kind === 'review' && <ReviewMark accent={metric.accent} />}

        {metric.kind === 'bars' && (
          <div className="flex h-14 w-14 flex-none items-end gap-[3px]">
            {metric.bars.map((h, k) => (
              <span
                key={k}
                className="sa-bar block flex-1 rounded-sm"
                style={{ '--h': `${h}%`, '--i': k, background: metric.accent, opacity: 0.35 + (k / metric.bars.length) * 0.65 }}
              />
            ))}
          </div>
        )}

        <div className="min-w-0">
          <div className="font-display text-[34px] font-semibold leading-none tracking-[-0.03em] text-white sm:text-[38px]">
            {metric.prefix || ''}
            {shown}
            {metric.suffix}
          </div>
          <div className="mt-2 truncate font-sans text-[15px] font-semibold text-slate-200">{metric.label}</div>
        </div>
      </div>

      <p className="mt-3.5 font-sans text-[14px] leading-relaxed text-slate-400">{metric.sub}</p>
    </li>
  )
}

/* ------------------------------------------------------------------- main */

/** Reveals once the block scrolls into view, so nothing animates off-screen. */
function useRevealed() {
  const ref = useRef(null)
  const [revealed, setRevealed] = useState(false)
  useEffect(() => {
    const el = ref.current
    if (!el || typeof IntersectionObserver === 'undefined') {
      setRevealed(true)
      return undefined
    }
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setRevealed(true)
          io.disconnect()
        }
      },
      { threshold: 0.15, rootMargin: '0px 0px -8% 0px' },
    )
    io.observe(el)
    return () => io.disconnect()
  }, [])
  return [ref, revealed]
}

export default function ImpactMetrics({ className = '' }) {
  const [ref, revealed] = useRevealed()

  return (
    <div ref={ref} data-revealed={revealed ? 'true' : 'false'} className={`sa-metrics ${className}`}>
      <style>{`
        .sa-metrics .sa-callout { opacity: 0; }
        .sa-metrics[data-revealed="true"] .sa-callout {
          animation: sa-callout-in 0.7s cubic-bezier(0.22,1,0.36,1) calc(0.1s + var(--i) * 0.14s) both;
        }
        @keyframes sa-callout-in {
          from { opacity: 0; transform: translateY(18px); filter: blur(6px); }
          to   { opacity: 1; transform: translateY(0);    filter: blur(0); }
        }

        .sa-metrics .sa-bar { height: 0; }
        .sa-metrics[data-revealed="true"] .sa-bar {
          animation: sa-bar-grow 0.9s cubic-bezier(0.22,1,0.36,1) calc(0.5s + var(--i) * 0.07s) forwards;
        }
        @keyframes sa-bar-grow { to { height: var(--h); } }

        @media (prefers-reduced-motion: reduce) {
          .sa-metrics .sa-callout { animation: none !important; opacity: 1 !important; }
          .sa-metrics .sa-bar { height: var(--h); animation: none !important; }
        }
      `}</style>

      {/* Three cards, centred rather than stretched across the full width. */}
      <ul className="mx-auto grid max-w-5xl gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {IMPACT.map((m, i) => (
          <ImpactCallout key={m.key} metric={m} revealed={revealed} index={i} />
        ))}
      </ul>
    </div>
  )
}
